// Imports
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { SetupIntent, StripePaymentElementOptions } from '@stripe/stripe-js';
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { GetSetupIntentResponse } from '../../../../../../shareables/types/api/main/website/member/payment-method/setup-intent/get';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import useHandleStripeConfirmationError from '../hooks/use-handle-stripe-confirmation-error';

const Wrapper = styled.div`
	padding-bottom: 1.25em;
`;


// Define the accepted ref
export interface CardSetupIntentHandlerRef {
	handleSubmit: (clientSecret: string) => Promise<SetupIntent | undefined>;
}


// Define the accepted props
interface Props {
	selectedPaymentMethodType: string;
}


// Function component
const CardSetupIntentHandler = forwardRef<CardSetupIntentHandlerRef, Props>(({ selectedPaymentMethodType }, ref) => {
	// Use state
	const [nameOnCard, setNameOnCard] = useState('');
	const [nameError, setNameError] = useState('');
	
	
	// Use Stripe hooks
	const stripe = useStripe();
	const elements = useElements();
	
	
	// Use hook
	const handleStripeConfirmationError = useHandleStripeConfirmationError();
	
	
	// Initialize
	const nameRequired = selectedPaymentMethodType === 'card';
	
	
	// Handle form submissions
	const handleSubmit = async (setupIntentClientSecret: GetSetupIntentResponse): Promise<SetupIntent | undefined> => {
		// Handle if Stripe.js didn't load for some reason
		if (!stripe || !elements) {
			throw new Error('Stripe.js hasn’t loaded');
		}
		
		if (!setupIntentClientSecret) {
			throw new Error('This should not be possible');
		}
		
		
		// Validate name on card
		if (nameRequired && !nameOnCard.trim()) {
			setNameError('This is required.');
			return;
		}
		
		
		// Trigger form validation
		const { error: submitError } = await elements.submit();
		
		if (submitError) {
			return;
		}
		
		
		// Confirm the setup intent using the details collected by the payment element
		const { error: confirmError, setupIntent } = await stripe.confirmSetup({
			elements: elements,
			clientSecret: setupIntentClientSecret,
			confirmParams: {
				return_url: `https://${window.location.hostname}/payments/methods`,
				payment_method_data: {
					allow_redisplay: 'always',
					billing_details: {
						name: nameOnCard,
					},
				},
			},
			redirect: 'if_required',
		});
		
		
		// Handle confirm errors
		if (confirmError) {
			handleStripeConfirmationError(confirmError, { confirmedWithPaymentElement: true });
			return;
		}
		
		
		// Return
		return setupIntent;
	};
	
	
	// Expose the method to parent
	useImperativeHandle(ref, () => ({
		handleSubmit,
	}));
	
	
	// Create options
	const options: StripePaymentElementOptions = useMemo(
		() => ({
			layout: 'tabs',
			terms: {
				card: 'never',
			},
		}),
		[]
	);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Wrapper>
				<Input
					name='nameOnCard'
					label='Name on card'
					type='text'
					onChange={(newValue) => {
						setNameOnCard(newValue);
						
						if (nameError) {
							setNameError('');
						}
					}}
					placeholder='John Doe'
					errorMessage={nameError}
					exclude
					required={nameRequired}
				/>
			</Wrapper>
			<PaymentElement options={options} />
		</React.Fragment>
	);
});


// @ts-ignore: displayName does exist
CardSetupIntentHandler.displayName = 'CardSetupIntentHandler';

export default CardSetupIntentHandler;
